import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useSiteLogo } from '../../context/SiteLogoContext';
import { warmBackend } from '../../config/api';
import { BRAND } from '../../config/brand';
import './Auth.css';

const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME = BRAND?.name || 'EVORA';

function Register() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register, googleLogin, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const { resolvedLogoUrl, logoAlt } = useSiteLogo();
  const navigate = useNavigate();
  const location = useLocation();

  // Retry handler
  const handleRetry = () => {
    setError('');
    setLoading(false);
    // Form will be re-submitted when user clicks submit again
  };

  // Check for redirect query parameter (e.g., ?redirect=cart)
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  // If redirect=cart, go to cart page after registration (cart will be merged automatically by backend)
  const redirectTo = redirectParam ? `/${redirectParam}` : '/';

  // Wake the backend when user lands on register page (Render cold start).
  // By the time they fill the form (30–60s), server is warm so registration won't timeout.
  useEffect(() => {
    warmBackend();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const getPasswordStrength = (password) => {
    if (!password) return null;
    if (password.length < 6) return { label: 'Too short', level: 0, color: '#E74C3C' };
    if (password.length < 8) return { label: 'Weak', level: 1, color: '#E74C3C' };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { label: 'Weak', level: 1, color: '#E74C3C' };
    if (score === 2) return { label: 'Fair', level: 2, color: '#F59E0B' };
    if (score === 3) return { label: 'Good', level: 3, color: '#3B82F6' };
    return { label: 'Strong', level: 4, color: '#059669' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    // Clear field error when user starts typing
    if (fieldErrors[e.target.name]) {
      setFieldErrors(prev => ({ ...prev, [e.target.name]: '' }));
    }
  };

  const handleFirstNameBlur = () => {
    const trimmed = formData.firstName.trim();
    if (trimmed && !/^[a-zA-Z\s'-]{1,50}$/.test(trimmed)) {
      setFieldErrors(prev => ({
        ...prev,
        firstName: 'Please enter a valid first name.',
      }));
    } else {
      setFieldErrors(prev => ({ ...prev, firstName: '' }));
    }
  };

  const handleLastNameBlur = () => {
    const trimmed = formData.lastName.trim();
    if (trimmed && !/^[a-zA-Z\s'-]{1,50}$/.test(trimmed)) {
      setFieldErrors(prev => ({
        ...prev,
        lastName: 'Please enter a valid last name.',
      }));
    } else {
      setFieldErrors(prev => ({ ...prev, lastName: '' }));
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setFieldErrors(prev => ({ 
        ...prev, 
        confirmPassword: 'Passwords do not match' 
      }));
    } else {
      setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      return 'Please enter your first name.';
    }
    if (formData.firstName.trim().length < 1) {
      return 'First name is required.';
    }
    if (!/^[a-zA-Z\s'-]{1,50}$/.test(formData.firstName.trim())) {
      return 'Please enter a valid first name.';
    }
    if (!formData.lastName.trim()) {
      return 'Please enter your last name.';
    }
    if (!/^[a-zA-Z\s'-]{1,50}$/.test(formData.lastName.trim())) {
      return 'Please enter a valid last name.';
    }
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      return 'Please enter a valid email address.';
    }
    if (!formData.password || formData.password.length < 6) {
      return 'Password must be at least 6 characters.';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    const result = await register(formData.firstName.trim(), formData.lastName.trim(), formData.email, formData.password);

    if (result.success) {
      const firstName = (formData.firstName || '').trim();
      if (redirectParam === 'cart') {
        showToast(firstName
          ? `You're in, ${firstName}! Your bag is ready whenever you are.`
          : "You're in! Your bag is ready whenever you are.", 'success');
      } else {
        showToast(firstName
          ? `Welcome to ${BRAND_NAME}, ${firstName}. We're glad to have you.`
          : `Welcome to ${BRAND_NAME}. We're glad to have you.`, 'success');
      }
      navigate(redirectTo, { replace: true });
      return; // Don't update state after navigation
    }

    // Map backend error codes to user-friendly messages
    const errorMessages = {
      'EMAIL_ALREADY_EXISTS': 'An account with this email already exists. Try signing in instead.',
      'INVALID_EMAIL': 'Please enter a valid email address.',
      'WEAK_PASSWORD': 'Password must be at least 6 characters.',
      'RATE_LIMITED': 'Too many attempts. Please wait a moment.',
      'SERVER_ERROR': 'Something went wrong. Please try again.',
      'NETWORK_ERROR': 'Connection failed. Check your internet and try again.',
      'TIMEOUT_ERROR': 'Request timed out. Please try again.',
    };

    if (result.code === 'EMAIL_ALREADY_EXISTS') {
      setError(
        <>
          An account already exists.{' '}
          <Link to="/login" className="auth-link">Sign in instead →</Link>
        </>
      );
    } else {
      setError(errorMessages[result.code] || result.message || 'Registration failed. Please try again.');
    }

    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      googleLogin();
    } catch (error) {
      setError('Google sign in failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Account | {BRAND_NAME}</title>
        <meta name="description" content={`Create your ${BRAND_NAME} account for faster checkout and exclusive access.`} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/register`} />
      </Helmet>
      
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <Link to="/" className="auth-logo" aria-label={`${BRAND.name} Home`}>
              <img src={resolvedLogoUrl} alt={logoAlt || BRAND_NAME} className="auth-logo-image" width="120" height="40" />
            </Link>
            <h1 className="auth-title">Create Account</h1>
            <p className="auth-subtitle">Join for exclusive access & faster checkout</p>
          </div>

        {error && (
          <div id="auth-error-message" className="auth-error" role="alert" aria-live="assertive">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            {(typeof error === 'string' && (error.includes('timeout') || error.includes('Network error'))) && (
              <button
                type="button"
                className="auth-error-retry"
                onClick={handleRetry}
                aria-label="Retry registration"
              >
                Retry
              </button>
            )}
          </div>
        )}

        {/* Google OAuth first for better conversion */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          className="auth-button auth-button--google"
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>

        <div className="auth-divider">
          <span>or register with email</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="auth-form-group">
            <label htmlFor="firstName" className="auth-label">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              onBlur={handleFirstNameBlur}
              className={`auth-input ${fieldErrors.firstName ? 'auth-input--error' : ''}`}
              placeholder="John"
              required
              autoComplete="given-name"
              autoFocus
              aria-describedby={error || fieldErrors.firstName ? 'auth-error-message' : undefined}
              aria-invalid={!!(error || fieldErrors.firstName)}
            />
            {fieldErrors.firstName && (
              <span className="auth-field-error">{fieldErrors.firstName}</span>
            )}
          </div>

          <div className="auth-form-group">
            <label htmlFor="lastName" className="auth-label">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              onBlur={handleLastNameBlur}
              className={`auth-input ${fieldErrors.lastName ? 'auth-input--error' : ''}`}
              placeholder="Doe"
              required
              autoComplete="family-name"
              aria-describedby={error || fieldErrors.lastName ? 'auth-error-message' : undefined}
              aria-invalid={!!(error || fieldErrors.lastName)}
            />
            {fieldErrors.lastName && (
              <span className="auth-field-error">{fieldErrors.lastName}</span>
            )}
          </div>

          <div className="auth-form-group">
            <label htmlFor="email" className="auth-label">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`auth-input ${fieldErrors.email ? 'auth-input--error' : ''}`}
              placeholder="your@email.com"
              required
              autoComplete="email"
              inputMode="email"
              aria-describedby={error ? 'auth-error-message' : undefined}
              aria-invalid={!!error}
            />
          </div>

          <div className="auth-form-group auth-form-group--password">
            <label htmlFor="password" className="auth-label">Password</label>
            <div className="auth-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`auth-input ${fieldErrors.password ? 'auth-input--error' : ''}`}
                placeholder="Min. 6 characters"
                required
                autoComplete="new-password"
                minLength="6"
                aria-describedby={error || fieldErrors.password ? 'auth-error-message' : undefined}
                aria-invalid={!!(error || fieldErrors.password)}
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {formData.password && passwordStrength && (
              <div className="auth-password-strength">
                <div className="auth-password-strength-bars">
                  {[1, 2, 3, 4].map(level => (
                    <div
                      key={level}
                      className="auth-password-strength-bar"
                      style={{
                        background: level <= passwordStrength.level 
                          ? passwordStrength.color 
                          : 'var(--color-gray-200)'
                      }}
                    />
                  ))}
                </div>
                <span 
                  className="auth-password-strength-label"
                  style={{ color: passwordStrength.color }}
                >
                  {passwordStrength.label}
                </span>
              </div>
            )}
          </div>

          <div className="auth-form-group auth-form-group--password">
            <label htmlFor="confirmPassword" className="auth-label">Confirm Password</label>
            <div className="auth-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleConfirmPasswordBlur}
                className={`auth-input ${fieldErrors.confirmPassword ? 'auth-input--error' : ''}`}
                placeholder="Repeat your password"
                required
                autoComplete="new-password"
                minLength="6"
                aria-describedby={error || fieldErrors.confirmPassword ? 'auth-error-message' : undefined}
                aria-invalid={!!(error || fieldErrors.confirmPassword)}
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => setShowConfirmPassword(prev => !prev)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.confirmPassword && (
              <span className="auth-field-error">{fieldErrors.confirmPassword}</span>
            )}
          </div>

          <button
            type="submit"
            className="auth-button auth-button--primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="auth-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Creating account...
              </>
            ) : 'Create Account'}
          </button>
        </form>

          <div className="auth-trust">
            <span className="auth-trust-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Secure
            </span>
            <span className="auth-trust-divider">·</span>
            <span className="auth-trust-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7z" />
                <path d="M6 21V9" />
              </svg>
              Free Shipping
            </span>
            <span className="auth-trust-divider">·</span>
            <span className="auth-trust-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Returns for size &amp; defective only
            </span>
          </div>

          <p className="auth-footer">
            Already have an account?{' '}
            <Link to="/login" className="auth-link auth-link--primary">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}

export default Register;
